import { useMemo, useState } from "react";
import { Page, DataTable } from "@iq/models";
import useOrderStore from "../states/order";
import OrderDetails from "../modals/OrderDetails/OrderDetails";
import "../styles/order.css";
import useIsSmallScreen from "../hooks/useIsSmallScreen";
function Orders({ onOpenOrderDetails = () => {} }) {
    const storedOrders = useOrderStore((state) => state.orders);
    const setOrders = useOrderStore((state) => state.setOrders);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const orders = useMemo(() => {
        if (Array.isArray(storedOrders)) {
            return storedOrders;
        }

        return Array.isArray(storedOrders?.orders) ? storedOrders.orders : [];
    }, [storedOrders]);

    const tableConfig = useMemo(() => ({
        columns: [
            {
                key: "id",
                label: "Order",
                accessor: "id",
                width: "0.8fr",
                formatter: (id) => `#${id}`,
                filterable: true,
            },
            {
                key: "customer_name",
                label: "Customer",
                accessor: (order) => order.customer_name || `Customer #${order.customer_id}`,
                width: "1fr",
                filterable: true,
            },
            {
                key: "total_amount",
                label: "Total",
                accessor: "total_amount",
                width: "1fr",
                formatter: (amount) => formatCurrency(amount),
                filterable: true,
            },
            {
                key: "status",
                label: "Status",
                accessor: "status",
                width: "1fr",
                filterable: true,
                quickValues: ["PAID", "PROCESSING", "SHIPPED", "COMPLETED", "CANCELLED"],
            },
            {
                key: "order_items",
                label: "Items",
                accessor: "order_items",
                width: "0.7fr",
                formatter: (items) => Array.isArray(items) ? items.length : 0,
                searchable: false,
                filterable: false,
            },
        ],
        emptyLabel: "No orders found.",
        searchPlaceholder: "Search orders",
    }), []);

    const mobileTableConfig = useMemo(()=> ({
        columns: [
            {
                key: "id",
                label: "Order",
                accessor: "id",
                width: "1fr",
                formatter: (id) => `#${id}`,
                filterable: true,
            },
            {
                key: "customer_name",
                label: "Customer",
                accessor: (order) => order.customer_name || `Customer #${order.customer_id}`,
                width: "3fr",
                filterable: true,
            },
            {
                key: "total_amount",
                label: "Total",
                accessor: "total_amount",
                width: "2fr",
                formatter: (amount) => formatCurrency(amount),
                filterable: true,
            },
            {
                key: "status",
                label: "Status",
                accessor: "status",
                width: "2fr",
                filterable: true,
                quickValues: ["PAID", "PROCESSING", "SHIPPED", "COMPLETED", "CANCELLED"],
            },
        ],
        emptyLabel: "No orders found.",
        searchPlaceholder: "Search orders",
    }), []);
    const isSmallScreen = useIsSmallScreen();

    return (
        <Page>
            <DataTable
                rowData={orders}
                rowKey={(order) => order.id}
                tableConfig={isSmallScreen ? mobileTableConfig : tableConfig}
                onRowClick={(order) => {
                    setSelectedOrder(order);
                    onOpenOrderDetails(order);
                }}
            />
            <OrderDetails
                isOpen={!!selectedOrder}
                order={selectedOrder}
                onClose={() => setSelectedOrder(null)}
                onTrackingNumberChange={(orderID, trackingNumber) => {
                    setOrders(orders.map((order) => (
                        order.id === orderID ? { ...order, tracking_number: trackingNumber } : order
                    )));
                }}
            />
        </Page>
    );
}

function formatCurrency(amount) {
    const cents = Number(amount);

    if (!Number.isFinite(cents)) {
        return "-";
    }

    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(cents / 100);
}

export default Orders;